<template>
  <div class="page-content">
    <div class="title">
      Catalog Pricing (B2 / Z)
    </div>
    <p class="subtitle">
      Prices for products with dependency B2/Z (virtual / dropship, no physical
      stock). These are read by the website and Typesense.
    </p>

    <div class="lp45-setting">
      <label>
        LP45 dealer profit (global)
        <input
          class="regular bordered"
          type="number"
          step="0.01"
          v-model.number="dealerProfitInput"
        />
      </label>
      <button
        class="btn btn-dark-button"
        :disabled="savingDealerProfit"
        @click="saveDealerProfit"
      >
        {{ savingDealerProfit ? "Saving..." : "Save" }}
      </button>
    </div>

    <div class="toolbar">
      <input
        class="regular bordered search"
        type="text"
        v-model="search"
        placeholder="Search by nkr_code or description"
        @keyup.enter="reload"
      />
      <select class="regular bordered" v-model="dependencia" @change="reload">
        <option :value="null">All (B2 & Z)</option>
        <option value="B2">B2</option>
        <option value="Z">Z</option>
      </select>
      <select class="regular bordered" v-model="priceLogic" @change="reload">
        <option :value="null">All logics</option>
        <option value="fixed">Fixed</option>
        <option value="calculated">Calculated</option>
        <option value="none">No price yet</option>
      </select>
      <button class="btn btn-dark-button" @click="reload">Search</button>
      <button
        class="btn btn-dark-button"
        :disabled="loadingRecalcPreview"
        @click="checkOutdatedPrices"
      >
        {{
          loadingRecalcPreview
            ? "Checking..."
            : "Check for outdated prices"
        }}
      </button>
    </div>

    <!-- Dry-run: diff entre lo persistido y lo que resultaría de recalcular
    todo lo que tiene price_logic = 'calculated' con los datos actuales. -->
    <div v-if="recalcPreview" class="recalc-panel">
      <div class="recalc-header">
        <b>{{ recalcChanged.length }}</b> product(s) would change.
        <span v-if="recalcErrors.length" class="recalc-errors">
          {{ recalcErrors.length }} skipped with errors.
        </span>
        <button class="btn btn-dark-button" @click="recalcPreview = null">
          Close
        </button>
      </div>

      <table v-if="recalcChanged.length" class="recalc-table">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                :checked="allRecalcSelected"
                @change="toggleAllRecalcSelected($event.target.checked)"
              />
            </th>
            <th>nkr_code</th>
            <th>Price group</th>
            <th>Online</th>
            <th>Repair shop</th>
            <th>Wholesale</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="entry in recalcChanged" :key="entry.nkrCode">
            <td>
              <input
                type="checkbox"
                v-model="recalcSelected"
                :value="entry.nkrCode"
              />
            </td>
            <td>{{ entry.nkrCode }}</td>
            <td>{{ entry.priceGroup }}</td>
            <td>
              {{ formatMoney(entry.current.online) }} →
              {{ formatMoney(entry.new.online) }}
            </td>
            <td>
              {{ formatMoney(entry.current.repairShop) }} →
              {{ formatMoney(entry.new.repairShop) }}
            </td>
            <td>
              {{ formatMoney(entry.current.wholesale) }} →
              {{ formatMoney(entry.new.wholesale) }}
            </td>
          </tr>
        </tbody>
      </table>

      <ul v-if="recalcErrors.length" class="recalc-error-list">
        <li v-for="entry in recalcErrors" :key="entry.nkrCode">
          <b>{{ entry.nkrCode }}</b> ({{ entry.priceGroup }}): {{ entry.error }}
        </li>
      </ul>

      <button
        v-if="recalcChanged.length"
        class="btn btn-success-button mt-2"
        :disabled="!recalcSelected.length || applyingRecalc"
        @click="applyOutdatedPrices"
      >
        {{
          applyingRecalc
            ? "Applying..."
            : `Apply to ${recalcSelected.length} selected`
        }}
      </button>
    </div>

    <div v-if="loading" class="loading">
      <BaseSmallSpinner />
    </div>

    <div v-else class="list">
      <div v-if="!items.length" class="empty">No products found.</div>
      <div class="row" v-for="item in items" :key="item.nkrCode">
        <div class="row-head" @click="toggle(item.nkrCode)">
          <div class="cell code">{{ item.nkrCode }}</div>
          <div class="cell dep">{{ item.dependencia }}</div>
          <div class="cell desc">{{ item.description }}</div>
          <div class="cell logic">
            <span :class="['badge', item.priceId ? 'ok' : 'missing']">
              {{ item.priceId ? item.priceLogic : "no price" }}
            </span>
          </div>
          <div class="cell price">{{ formatMoney(item.online) }}</div>
          <div class="cell toggle">
            {{ expanded === item.nkrCode ? "▲" : "▼" }}
          </div>
        </div>

        <div class="editor" v-if="expanded === item.nkrCode">
          <CatalogPriceForm
            v-model="draft"
            :priceGroups="priceGroups"
            :masterListPrice="Number(item.listPrice) || 0"
            :dealerProfit="dealerProfit"
          />

          <div class="editor-actions">
            <button
              class="btn btn-success-button"
              :disabled="saving"
              @click="save(item)"
            >
              {{ saving ? "Saving..." : "Save" }}
            </button>
            <button class="btn btn-dark-button" @click="expanded = null">
              Cancel
            </button>
            <button
              v-if="item.priceId"
              class="btn btn-cancel-button"
              @click="remove(item)"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="pagination" v-if="!loading && total > limit">
      <button
        class="btn btn-dark-button"
        :disabled="page === 1"
        @click="prevPage"
      >
        Prev
      </button>
      <span>Page {{ page }} / {{ totalPages }}</span>
      <button
        class="btn btn-dark-button"
        :disabled="page >= totalPages"
        @click="nextPage"
      >
        Next
      </button>
    </div>
  </div>
</template>

<script>
import {
  loadCatalogPrices,
  saveCatalogPrice,
  deleteCatalogPrice,
  previewRecalculateCatalogPrices,
  applyRecalculateCatalogPrices
} from "@/api/store/CatalogPricingService.js";
import { loadPriceGroups } from "@/api/store/PriceGroupsService.js";
import { getSettings, updateSetting } from "@/api/store/SettingsService.js";
import CatalogPriceForm from "@/components/product/CatalogPriceForm.vue";
import { DEFAULT_DEALER_PROFIT } from "@/utils/priceGroupCalculator.js";

const DEALER_PROFIT_SETTING_KEY = "lp45_dealer_profit";

export default {
  name: "CatalogPricing",
  components: { CatalogPriceForm },
  data() {
    return {
      items: [],
      priceGroups: [],
      search: "",
      dependencia: null,
      priceLogic: null,
      page: 1,
      limit: 50,
      total: 0,
      loading: true,
      saving: false,
      expanded: null,
      draft: {},
      dealerProfit: DEFAULT_DEALER_PROFIT,
      dealerProfitInput: DEFAULT_DEALER_PROFIT,
      savingDealerProfit: false,
      loadingRecalcPreview: false,
      applyingRecalc: false,
      recalcPreview: null,
      recalcSelected: []
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.limit));
    },
    recalcChanged() {
      return (this.recalcPreview || []).filter(
        e => !e.error && e.changed
      );
    },
    recalcErrors() {
      return (this.recalcPreview || []).filter(e => e.error);
    },
    allRecalcSelected() {
      return (
        this.recalcChanged.length > 0 &&
        this.recalcSelected.length === this.recalcChanged.length
      );
    }
  },
  mounted() {
    loadPriceGroups().then(res => {
      this.priceGroups = res;
    });
    this.loadDealerProfit();
    this.load();
  },
  methods: {
    loadDealerProfit() {
      getSettings(DEALER_PROFIT_SETTING_KEY).then(res => {
        if (res && res.value !== undefined && res.value !== null) {
          this.dealerProfit = Number(res.value);
          this.dealerProfitInput = this.dealerProfit;
        }
      });
    },
    async saveDealerProfit() {
      this.savingDealerProfit = true;
      try {
        await updateSetting(
          DEALER_PROFIT_SETTING_KEY,
          String(this.dealerProfitInput),
          1
        );
        this.dealerProfit = this.dealerProfitInput;
        this.toast("Dealer profit updated", "success");
      } catch (e) {
        this.toast("Error saving dealer profit", "error");
      } finally {
        this.savingDealerProfit = false;
      }
    },
    load() {
      this.loading = true;
      loadCatalogPrices({
        search: this.search,
        page: this.page,
        limit: this.limit,
        dependencia: this.dependencia,
        priceLogic: this.priceLogic
      })
        .then(res => {
          this.items = res.data || [];
          this.total = res.total || 0;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.toast("Error loading catalog prices", "error");
        });
    },
    reload() {
      this.page = 1;
      this.expanded = null;
      this.load();
    },
    prevPage() {
      if (this.page > 1) {
        this.page--;
        this.expanded = null;
        this.load();
      }
    },
    nextPage() {
      if (this.page < this.totalPages) {
        this.page++;
        this.expanded = null;
        this.load();
      }
    },
    toggle(nkrCode) {
      if (this.expanded === nkrCode) {
        this.expanded = null;
        return;
      }
      const item = this.items.find(i => i.nkrCode === nkrCode);
      this.draft = {
        priceLogic: item.priceLogic || "fixed",
        priceGroup: item.priceGroup || "",
        cost:
          item.cost != null
            ? Number(item.cost)
            : item.masterCost != null
            ? Number(item.masterCost)
            : 0,
        online: Number(item.online) || 0,
        repairShop: Number(item.repairShop) || 0,
        wholesale: Number(item.wholesale) || 0,
        precio4: Number(item.precio4) || 0,
        precio5: Number(item.precio5) || 0,
        precio6: Number(item.precio6) || 0,
        onlineMinPrice: Number(item.onlineMinPrice) || 0,
        repairShopMinPrice: Number(item.repairShopMinPrice) || 0,
        wholesaleMinPrice: Number(item.wholesaleMinPrice) || 0,
        precio4MinPrice: Number(item.precio4MinPrice) || 0,
        precio5MinPrice: Number(item.precio5MinPrice) || 0,
        precio6MinPrice: Number(item.precio6MinPrice) || 0
      };
      this.expanded = nkrCode;
    },
    async save(item) {
      this.saving = true;
      try {
        const saved = await saveCatalogPrice({
          nkrCode: item.nkrCode,
          ...this.draft
        });
        // El servidor devuelve la fila materializada; refrescamos la fila local.
        Object.assign(item, saved, { priceId: saved.id });
        this.toast("Catalog price saved", "success");
        this.expanded = null;
      } catch (e) {
        const msg =
          (e.response && e.response.data) || "Error saving catalog price";
        this.toast(typeof msg === "string" ? msg : "Error saving", "error");
      } finally {
        this.saving = false;
      }
    },
    async checkOutdatedPrices() {
      this.loadingRecalcPreview = true;
      this.recalcPreview = null;
      this.recalcSelected = [];
      try {
        const res = await previewRecalculateCatalogPrices();
        this.recalcPreview = res.data || [];
        this.recalcSelected = this.recalcChanged.map(e => e.nkrCode);
        if (!this.recalcChanged.length && !this.recalcErrors.length) {
          this.toast("All calculated prices are already up to date", "success");
          this.recalcPreview = null;
        }
      } catch (e) {
        this.toast("Error checking outdated prices", "error");
      } finally {
        this.loadingRecalcPreview = false;
      }
    },
    toggleAllRecalcSelected(checked) {
      this.recalcSelected = checked ? this.recalcChanged.map(e => e.nkrCode) : [];
    },
    async applyOutdatedPrices() {
      if (!this.recalcSelected.length) return;
      if (
        !confirm(
          `Apply recalculated prices to ${this.recalcSelected.length} product(s)? This overwrites their current online/repair_shop/wholesale.`
        )
      ) {
        return;
      }
      this.applyingRecalc = true;
      try {
        const res = await applyRecalculateCatalogPrices(this.recalcSelected);
        this.toast(
          `${(res.updated || []).length} updated${
            (res.skipped || []).length
              ? `, ${res.skipped.length} skipped`
              : ""
          }`,
          "success"
        );
        this.recalcPreview = null;
        this.recalcSelected = [];
        this.load();
      } catch (e) {
        this.toast("Error applying recalculated prices", "error");
      } finally {
        this.applyingRecalc = false;
      }
    },
    async remove(item) {
      if (!confirm(`Delete catalog price for ${item.nkrCode}?`)) {
        return;
      }
      try {
        await deleteCatalogPrice(item.nkrCode);
        this.toast("Catalog price deleted", "success");
        this.expanded = null;
        this.load();
      } catch (e) {
        this.toast("Error deleting catalog price", "error");
      }
    },
    formatMoney(v) {
      if (v == null || v === "") return "—";
      return `$${Number(v).toFixed(2)}`;
    },
    toast(message, type) {
      this.$toast.open({ message, type, position: "top-right" });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";

.title {
  font-size: 1.3rem;
  font-weight: 700;
}
.subtitle {
  color: grey;
  margin-bottom: 1rem;
}
.lp45-setting {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 1rem;
  padding: 10px;
  border: 1px dashed grey;
  border-radius: 4px;
  label {
    display: flex;
    flex-direction: column;
    font-size: 0.85rem;
    gap: 4px;
  }
}
.toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 1rem;
  .search {
    flex: 1;
    min-width: 200px;
  }
}
.recalc-panel {
  border: 1px solid grey;
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 1rem;
  background: #fffbea;
  .recalc-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    .recalc-errors {
      color: #a94442;
    }
    button {
      margin-left: auto;
    }
  }
  .recalc-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 8px;
    th,
    td {
      border: 1px solid #ddd;
      padding: 4px 8px;
      text-align: left;
      font-size: 0.85rem;
    }
  }
  .recalc-error-list {
    font-size: 0.8rem;
    color: #a94442;
    margin: 0 0 8px 0;
    padding-left: 1.2rem;
  }
}
.list {
  .empty {
    padding: 2rem;
    text-align: center;
    color: grey;
  }
  .row {
    border: 1px solid grey;
    border-radius: 4px;
    margin: 8px 0;
    overflow: hidden;
  }
  .row-head {
    display: grid;
    grid-template-columns: 1.4fr 0.5fr 2fr 1fr 1fr 40px;
    align-items: center;
    padding: 10px;
    cursor: pointer;
    &:hover {
      background-color: $tableTRHoverBG;
      color: $tableTRHoverText;
    }
    .cell {
      padding: 0 5px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .badge {
      padding: 2px 8px;
      border-radius: 10px;
      font-size: 0.8rem;
      &.ok {
        background: #d4edda;
        color: #155724;
      }
      &.missing {
        background: #f8d7da;
        color: #721c24;
      }
    }
  }
  .editor {
    padding: 12px;
    border-top: 1px dashed grey;
    background: #fafafa;
    .editor-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
      margin-bottom: 10px;
      label {
        display: flex;
        flex-direction: column;
        font-size: 0.85rem;
        gap: 4px;
      }
    }
    .hint {
      font-size: 0.8rem;
      color: #555;
      margin-bottom: 8px;
    }
    .tiers {
      width: 100%;
      border-collapse: collapse;
      th,
      td {
        border: 1px solid #ddd;
        padding: 6px 8px;
        text-align: left;
      }
      input {
        width: 120px;
      }
      input:disabled {
        background: #eee;
      }
    }
    .editor-actions {
      display: flex;
      gap: 8px;
      margin-top: 10px;
    }
  }
}
.pagination {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
  margin-top: 1rem;
}
.loading {
  padding: 2rem;
  text-align: center;
}
</style>
